import { Snackbar, SnackbarContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { styled, useTheme } from "@mui/material/styles";

type TransactionStatus = "error" | "success" | "warning" | "info";

interface TransactionSnackbarProps {
  open: boolean;
  onClose: () => void;
  status: TransactionStatus;
  title: string;
  message?: string;
  onTap?: () => void;
}

const StatusIcons = {
  error: CloseIcon,
  success: CheckIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

const StatusColors = {
  error: (theme: any) => theme.palette.error.main,
  success: (theme: any) => theme.palette.success.main,
  warning: (theme: any) => theme.palette.warning.main,
  info: (theme: any) => theme.palette.info.main,
};

// Create interfaces for custom props
interface StyledSnackbarContentProps {
  statusColor: string;
}

// Use the interface in the styled component
const StyledSnackbarContent = styled(SnackbarContent, {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<StyledSnackbarContentProps>(({ theme, statusColor }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderLeft: `4px solid ${statusColor}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  "& .MuiSnackbarContent-message": {
    padding: 0,
  },
}));

const IconContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<{ statusColor: string }>(({ theme, statusColor }) => ({
  display: "flex",
  height: 32,
  width: 32,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: statusColor,
  color: theme.palette.common.white,
}));

export default function TransactionSnackbar({
  open,
  onClose,
  status,
  title,
  message = "Tap to view this transaction",
  onTap,
}: TransactionSnackbarProps) {
  const theme = useTheme();
  const Icon = StatusIcons[status];
  const statusColor = StatusColors[status](theme);

  const handleClick = () => {
    onTap?.();
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <StyledSnackbarContent
        statusColor={statusColor}
        message={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IconContainer statusColor={statusColor}>
              <Icon fontSize="small" />
            </IconContainer>
            <div>
              <div style={{ fontWeight: 500 }}>{title}</div>
              {message && (
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  {message}
                </div>
              )}
            </div>
          </div>
        }
        action={
          <IconButton size="small" color="inherit" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        onClick={handleClick}
      />
    </Snackbar>
  );
}
